<template>
  <main class="quiz-manage-page">
    <div class="manage-ambient" aria-hidden="true"></div>

    <header class="manage-header">
      <button type="button" class="header-button" aria-label="Zum Dashboard" @click="$router.push('/dashboard')"><v-icon>mdi-arrow-left</v-icon></button>
      <div class="brand-lockup"><img :src="brandLogo" alt="WELL COME FIT" width="1200" height="931" /><div><strong>LIVE QUIZ</strong><small>Quiz Management</small></div></div>
      <div class="header-actions">
        <v-btn v-if="canPlay" text color="#c8d1cc" class="player-link" @click="$router.push('/quiz')"><v-icon left>mdi-cellphone</v-icon>Teilnehmen</v-btn>
        <button type="button" class="header-button" aria-label="Daten aktualisieren" @click="loadData"><v-icon>mdi-refresh</v-icon></button>
      </div>
    </header>

    <section class="manage-shell">
      <div class="manage-hero">
        <div>
          <span class="manage-kicker"><i></i> LIVE QUIZ STUDIO</span>
          <h1>Quiz erstellen.<br /><em>Menschen begeistern.</em></h1>
          <p>Fragen vorbereiten, eine Live-Runde eröffnen und Resultate in Echtzeit erleben.</p>
        </div>
        <v-btn color="#58d388" class="create-button" x-large depressed @click="createQuiz"><v-icon left>mdi-plus</v-icon>Neues Quiz</v-btn>
      </div>

      <section class="metric-grid" aria-label="Quiz Übersicht">
        <div class="metric-card"><span class="metric-icon published"><v-icon>mdi-broadcast</v-icon></span><div><small>Veröffentlicht</small><strong>{{ statusCount("published") }}</strong><p>bereit zum Starten</p></div></div>
        <div class="metric-card"><span class="metric-icon draft"><v-icon>mdi-pencil-outline</v-icon></span><div><small>Entwürfe</small><strong>{{ statusCount("draft") }}</strong><p>in Bearbeitung</p></div></div>
        <div class="metric-card"><span class="metric-icon live"><v-icon>mdi-access-point</v-icon></span><div><small>Aktiv</small><strong>{{ activeSessions.length }}</strong><p>laufende Sessions</p></div></div>
        <div class="metric-card"><span class="metric-icon history"><v-icon>mdi-history</v-icon></span><div><small>Gespielt</small><strong>{{ history.length }}</strong><p>gespeicherte Runden</p></div></div>
      </section>

      <section v-if="activeSessions.length" class="active-sessions-section">
        <div class="section-heading"><div><span>AKTIV &amp; PAUSIERT</span><h2>Laufende Sessions</h2><p>Du kannst jederzeit wieder in die Leiteransicht einsteigen.</p></div></div>
        <div class="active-session-grid">
          <button v-for="session in activeSessions" :key="session._id" type="button" class="active-session-card" :class="{ paused: session.state === 'paused' || session.pauseRequested }" @click="resumeSession(session)">
            <span class="live-orbit"><i></i><v-icon>{{ session.state === "paused" ? "mdi-pause" : session.pauseRequested ? "mdi-timer-pause-outline" : "mdi-access-point" }}</v-icon></span>
            <div><small>{{ stateLabel(session) }}</small><strong>{{ session.quizTitle }}</strong><p><span>{{ formatCode(session.joinCode) }}</span> · {{ session.participantCount }} {{ session.state === "paused" ? "gerade online" : "dabei" }}</p></div>
            <v-icon>mdi-arrow-right</v-icon>
          </button>
        </div>
      </section>

      <section class="library-section">
        <div class="section-heading library-heading">
          <div><span>QUIZ-BIBLIOTHEK</span><h2>Deine Quiz</h2><p>Entwürfe bearbeiten, Quiz veröffentlichen oder direkt live starten.</p></div>
          <div class="filter-pills" role="tablist" aria-label="Quiz filtern">
            <button v-for="filter in filters" :key="filter.value" type="button" :class="{ active: activeFilter === filter.value }" @click="activeFilter = filter.value">
              {{ filter.label }}<span>{{ filterCount(filter.value) }}</span>
            </button>
          </div>
        </div>

        <div v-if="loading" class="quiz-grid">
          <v-skeleton-loader v-for="index in 4" :key="index" dark type="image, article, actions" class="quiz-skeleton" />
        </div>
        <div v-else-if="filteredQuizzes.length" class="quiz-grid">
          <article v-for="quiz in filteredQuizzes" :key="quiz._id" class="quiz-card" :class="`card-${quiz.status}`">
            <div class="quiz-card-top">
              <span class="quiz-card-icon"><v-icon>mdi-head-question-outline</v-icon></span>
              <span class="status-pill" :class="`status-${quiz.status}`"><i></i>{{ statusLabel(quiz.status) }}</span>
              <v-menu offset-y left content-class="quiz-card-menu">
                <template v-slot:activator="{ on, attrs }">
                  <v-btn icon small class="card-menu-button" aria-label="Weitere Aktionen" v-bind="attrs" v-on="on"><v-icon>mdi-dots-horizontal</v-icon></v-btn>
                </template>
                <v-list dark dense>
                  <v-list-item @click="editQuiz(quiz)"><v-list-item-icon><v-icon>mdi-pencil-outline</v-icon></v-list-item-icon><v-list-item-title>Bearbeiten</v-list-item-title></v-list-item>
                  <v-list-item @click="duplicateQuiz(quiz)"><v-list-item-icon><v-icon>mdi-content-copy</v-icon></v-list-item-icon><v-list-item-title>Duplizieren</v-list-item-title></v-list-item>
                  <v-list-item v-if="quiz.status !== 'archived'" @click="archiveQuiz(quiz)"><v-list-item-icon><v-icon color="#ff8994">mdi-archive-outline</v-icon></v-list-item-icon><v-list-item-title>Archivieren</v-list-item-title></v-list-item>
                </v-list>
              </v-menu>
            </div>
            <div class="quiz-card-copy">
              <h3>{{ quiz.title }}</h3>
              <p>{{ quiz.description || "Keine Beschreibung hinterlegt." }}</p>
            </div>
            <div class="quiz-facts">
              <span><v-icon>mdi-message-text-fast-outline</v-icon>{{ quiz.questions.length }} {{ quiz.questions.length === 1 ? "Frage" : "Fragen" }}</span>
              <span><v-icon>mdi-clock-outline</v-icon>{{ totalDuration(quiz) }} Sek.</span>
            </div>
            <div class="quiz-card-footer">
              <small>Aktualisiert {{ formatRelativeDate(quiz.updatedAt) }}</small>
              <div>
                <v-btn icon class="edit-button" aria-label="Quiz bearbeiten" @click="editQuiz(quiz)"><v-icon>mdi-pencil-outline</v-icon></v-btn>
                <v-btn
                  v-if="quiz.status === 'published'"
                  color="#58d388"
                  class="start-button"
                  depressed
                  :loading="startingQuizId === quiz._id"
                  @click="startSession(quiz)">
                  <v-icon left>mdi-play</v-icon>Live starten
                </v-btn>
                <v-btn v-else outlined color="#aeb8b2" class="start-button" @click="editQuiz(quiz)">{{ quiz.status === "archived" ? "Öffnen" : "Fertigstellen" }}</v-btn>
              </div>
            </div>
          </article>
        </div>
        <div v-else class="empty-library">
          <span><v-icon>mdi-head-question-outline</v-icon></span>
          <h3>{{ activeFilter === "all" ? "Noch kein Quiz erstellt" : "In diesem Bereich ist noch nichts" }}</h3>
          <p>Mit einem neuen Quiz kannst du Fragen vorbereiten und später live spielen.</p>
          <v-btn color="#58d388" depressed large @click="createQuiz"><v-icon left>mdi-plus</v-icon>Quiz erstellen</v-btn>
        </div>
      </section>

      <section v-if="history.length" class="history-section">
        <div class="section-heading"><div><span>VERLAUF</span><h2>Letzte Quiz-Runden</h2><p>Resultate bleiben geordnet und für Manager einsehbar.</p></div></div>
        <div class="history-list">
          <button v-for="session in history.slice(0, 8)" :key="session._id" type="button" @click="openResults(session)">
            <span class="history-icon"><v-icon>{{ historyIcon(session.state) }}</v-icon></span>
            <div><strong>{{ session.quizTitle }}</strong><small>{{ formatDate(session.finishedAt || session.createdAt) }}</small></div>
            <span class="history-fact"><strong>{{ session.participantCount }}</strong><small>Teilnehmer</small></span>
            <span class="history-code">{{ formatCode(session.joinCode) }}</span>
            <v-icon>mdi-chevron-right</v-icon>
          </button>
        </div>
      </section>
    </section>

    <LiveQuizEditorDialog v-model="editorOpen" :quiz="selectedQuiz" :saving="savingQuiz" @save="saveQuiz" />

    <v-dialog v-model="resultsOpen" max-width="64rem" scrollable content-class="quiz-results-dialog">
      <v-card dark class="results-card">
        <header class="results-header">
          <span><v-icon>mdi-trophy-outline</v-icon></span>
          <div><small>QUIZ-RESULTATE</small><h2>{{ selectedResults && selectedResults.session ? selectedResults.session.quizTitle : "Resultate" }}</h2></div>
          <v-btn icon aria-label="Resultate schliessen" @click="resultsOpen = false"><v-icon>mdi-close</v-icon></v-btn>
        </header>
        <v-card-text class="results-body">
          <div v-if="loadingResults" class="results-loading"><v-progress-circular indeterminate color="#58d388" /><p>Resultate werden geladen.</p></div>
          <template v-else-if="selectedResults">
            <div class="results-summary">
              <div><span>{{ selectedResults.participants.length }}</span><small>Teilnehmer</small></div>
              <div><span>{{ selectedResults.session.totalQuestions }}</span><small>Fragen</small></div>
              <div><span>{{ formatCode(selectedResults.session.joinCode) }}</span><small>Session-Code</small></div>
            </div>

            <div class="results-tabs" role="tablist" aria-label="Quiz-Resultate anzeigen">
              <button type="button" :class="{ active: resultsTab === 'review' }" role="tab" :aria-selected="resultsTab === 'review'" @click="resultsTab = 'review'">
                <v-icon>mdi-message-text-fast-outline</v-icon>
                <span>Abschlussbesprechung</span>
                <small>{{ (selectedResults.questionReview || []).length }}</small>
              </button>
              <button type="button" :class="{ active: resultsTab === 'ranking' }" role="tab" :aria-selected="resultsTab === 'ranking'" @click="resultsTab = 'ranking'">
                <v-icon>mdi-trophy-outline</v-icon>
                <span>Rangliste</span>
                <small>{{ selectedResults.participants.length }}</small>
              </button>
            </div>

            <div v-if="resultsTab === 'review'" class="question-review-flow">
              <template v-if="currentReviewQuestion">
                <div class="review-flow-progress">
                  <div>
                    <strong>Frage {{ reviewQuestionIndex + 1 }} von {{ reviewQuestions.length }}</strong>
                    <span>{{ reviewQuestionTypeLabel(currentReviewQuestion.type) }}</span>
                  </div>
                  <div aria-hidden="true"><i :style="{ width: `${((reviewQuestionIndex + 1) / reviewQuestions.length) * 100}%` }"></i></div>
                </div>

                <article :key="currentReviewQuestion._id || reviewQuestionIndex" class="review-question-card">
                  <header class="review-question-header">
                    <span>{{ reviewQuestionIndex + 1 }}</span>
                    <div>
                      <small>{{ reviewQuestionTypeLabel(currentReviewQuestion.type).toUpperCase() }} · {{ currentReviewQuestion.timeLimitSeconds }} SEKUNDEN</small>
                      <strong>{{ currentReviewQuestion.text }}</strong>
                    </div>
                    <div class="review-response-total">
                      <strong>{{ currentReviewQuestion.responseCount }}</strong>
                      <small>Antworten</small>
                    </div>
                  </header>

                  <div v-if="currentReviewQuestion.type === 'estimate'" class="review-estimate-answer">
                    <span><v-icon>mdi-bullseye-arrow</v-icon></span>
                    <div>
                      <small>RICHTIGER ZIELWERT</small>
                      <strong>{{ formatEstimate(currentReviewQuestion.targetValue) }} <em v-if="currentReviewQuestion.unit">{{ currentReviewQuestion.unit }}</em></strong>
                      <p>{{ reviewEstimateRule(currentReviewQuestion) }}</p>
                    </div>
                  </div>
                  <div v-else class="review-answer-grid">
                    <div
                      v-for="(answer, answerIndex) in currentReviewQuestion.answers"
                      :key="answer._id || answerIndex"
                      class="review-answer"
                      :class="[reviewAnswerTone(answerIndex), { correct: answer.correct }]">
                      <span class="review-answer-shape"><v-icon>{{ reviewAnswerIcon(answerIndex) }}</v-icon></span>
                      <div>
                        <strong>{{ answer.text }}</strong>
                        <small>{{ answer.responseCount }} {{ Number(answer.responseCount) === 1 ? "Auswahl" : "Auswahlen" }}</small>
                      </div>
                      <span v-if="answer.correct" class="review-correct-badge"><v-icon>mdi-check</v-icon>Richtig</span>
                    </div>
                  </div>

                  <footer class="review-question-footer">
                    <span><v-icon>mdi-account-check-outline</v-icon>{{ currentReviewQuestion.responseCount }} abgegeben</span>
                    <span><v-icon>mdi-check-decagram-outline</v-icon>{{ currentReviewQuestion.correctResponseCount }} vollständig richtig</span>
                    <span v-if="currentReviewQuestion.unansweredCount"><v-icon>mdi-account-question-outline</v-icon>{{ currentReviewQuestion.unansweredCount }} ohne Antwort</span>
                  </footer>

                  <section class="review-participant-section">
                    <button
                      type="button"
                      class="review-participant-toggle"
                      :aria-expanded="String(reviewAnswersVisible)"
                      @click="reviewAnswersVisible = !reviewAnswersVisible">
                      <span><v-icon>mdi-account-details-outline</v-icon></span>
                      <div>
                        <strong>{{ reviewAnswersVisible ? "Antworten ausblenden" : "Antworten der Teilnehmenden anzeigen" }}</strong>
                        <small>Optional: Antworten einzeln gemeinsam durchgehen</small>
                      </div>
                      <em>{{ currentReviewQuestion.responseCount }}</em>
                      <v-icon>{{ reviewAnswersVisible ? "mdi-chevron-up" : "mdi-chevron-down" }}</v-icon>
                    </button>

                    <div v-if="reviewAnswersVisible" class="review-participant-list">
                      <div
                        v-for="participant in currentReviewParticipants"
                        :key="participant.participantId"
                        class="review-participant-row"
                        :class="{ unanswered: !participant.answered }">
                        <span class="review-participant-avatar">{{ initials(participant.displayName) }}</span>
                        <div>
                          <strong>{{ participant.displayName }}</strong>
                          <small>{{ participant.answerText }}</small>
                        </div>
                        <span v-if="participant.answered" class="review-participant-status" :class="{ correct: participant.correct }">
                          <v-icon>{{ participant.correct ? "mdi-check-circle" : "mdi-close-circle-outline" }}</v-icon>
                          {{ participant.correct ? "Richtig" : "Nicht richtig" }}
                        </span>
                        <span v-else class="review-participant-status"><v-icon>mdi-minus-circle-outline</v-icon>Keine Antwort</span>
                      </div>
                      <div v-if="!currentReviewParticipants.length" class="review-participant-empty">
                        <v-icon>mdi-account-off-outline</v-icon><span>Bei dieser Runde haben keine Personen teilgenommen.</span>
                      </div>
                    </div>
                  </section>
                </article>

                <nav class="review-navigation" aria-label="Fragen der Abschlussbesprechung">
                  <button type="button" class="review-nav-secondary" :disabled="reviewQuestionIndex === 0" @click="previousReviewQuestion">
                    <v-icon>mdi-arrow-left</v-icon><span>Zurück</span>
                  </button>
                  <span><strong>{{ reviewQuestionIndex + 1 }} / {{ reviewQuestions.length }}</strong><small>Fragen</small></span>
                  <button type="button" class="review-nav-primary" @click="nextReviewQuestion">
                    <span>{{ reviewQuestionIndex === reviewQuestions.length - 1 ? "Zur Rangliste" : "Weiter" }}</span><v-icon>mdi-arrow-right</v-icon>
                  </button>
                </nav>
              </template>
              <div v-else class="results-empty">
                <v-icon>mdi-message-question-outline</v-icon><p>Für diese Runde sind keine Fragen gespeichert.</p>
              </div>
            </div>

            <template v-else>
              <div v-if="selectedResults.participants.length" class="results-ranking">
                <div v-for="participant in selectedResults.participants" :key="participant._id" :class="`rank-${participant.rank}`">
                  <span class="result-rank">{{ participant.rank }}</span>
                  <span class="result-avatar">{{ initials(participant.displayName) }}</span>
                  <div><strong>{{ participant.displayName }}</strong><small>{{ participant.correctCount }} von {{ selectedResults.session.totalQuestions }} richtig</small></div>
                  <strong class="result-score">{{ formatScore(participant.score) }}</strong>
                </div>
              </div>
              <div v-else class="results-empty"><v-icon>mdi-account-off-outline</v-icon><p>Bei dieser Runde haben keine Personen teilgenommen.</p></div>
            </template>
          </template>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-snackbar v-model="toastVisible" :color="toastType === 'error' ? '#3a171b' : '#12311f'" rounded timeout="4500" bottom>
      <v-icon left :color="toastType === 'error' ? '#ff8a94' : '#58d388'">{{ toastType === "error" ? "mdi-alert-circle-outline" : "mdi-check-circle-outline" }}</v-icon>{{ toastMessage }}
    </v-snackbar>
  </main>
</template>

<script>
import api from "@/api"
import { brandLogo } from "@/constants/brandAssets"
import userService from "@/services/userService"
import LiveQuizEditorDialog from "./components/LiveQuizEditorDialog.vue"

export default {
  name: "LiveQuizManage",
  components: { LiveQuizEditorDialog },
  data() {
    return {
      brandLogo,
      quizzes: [],
      activeSessions: [],
      history: [],
      loading: true,
      activeFilter: "all",
      filters: [
        { label: "Alle", value: "all" },
        { label: "Live bereit", value: "published" },
        { label: "Entwürfe", value: "draft" },
        { label: "Archiv", value: "archived" },
      ],
      editorOpen: false,
      selectedQuiz: null,
      savingQuiz: false,
      startingQuizId: null,
      resultsOpen: false,
      resultsTab: "review",
      reviewQuestionIndex: 0,
      reviewAnswersVisible: false,
      loadingResults: false,
      selectedResults: null,
      toastVisible: false,
      toastMessage: "",
      toastType: "success",
    }
  },
  computed: {
    canPlay() {
      return userService.hasRight("g_quiz")
    },
    filteredQuizzes() {
      if (this.activeFilter === "all") return this.quizzes
      return this.quizzes.filter((quiz) => quiz.status === this.activeFilter)
    },
    reviewQuestions() {
      return this.selectedResults && Array.isArray(this.selectedResults.questionReview)
        ? this.selectedResults.questionReview
        : []
    },
    currentReviewQuestion() {
      return this.reviewQuestions[this.reviewQuestionIndex] || null
    },
    currentReviewParticipants() {
      if (!this.currentReviewQuestion || !this.selectedResults) return []
      const question = this.currentReviewQuestion
      const questionId = String(question._id || "")
      const questionIndex = Number(question.questionIndex)
      const answers = Array.isArray(this.selectedResults.answers) ? this.selectedResults.answers : []
      const participants = Array.isArray(this.selectedResults.participants) ? this.selectedResults.participants : []
      const answerTextById = new Map((question.answers || []).map((answer) => [String(answer._id), answer.text]))
      const answerByParticipant = new Map()

      answers.forEach((answer) => {
        const matchesQuestion =
          (questionId && String(answer.questionId || "") === questionId) || Number(answer.questionIndex) === questionIndex
        if (matchesQuestion) answerByParticipant.set(String(answer.participant), answer)
      })

      return participants.map((participant) => {
        const answer = answerByParticipant.get(String(participant._id))
        if (!answer) {
          return {
            participantId: participant._id,
            displayName: participant.displayName,
            answered: false,
            answerText: "Keine Antwort abgegeben",
            correct: false,
          }
        }

        let answerText = "Antwort gespeichert"
        if (question.type === "estimate") {
          const unit = question.unit ? ` ${question.unit}` : ""
          answerText = `${this.formatEstimate(answer.estimatedValue)}${unit}`
        } else {
          const answerIds = Array.isArray(answer.answerIds) && answer.answerIds.length
            ? answer.answerIds
            : answer.answerId
              ? [answer.answerId]
              : []
          const labels = answerIds.map((answerId) => answerTextById.get(String(answerId))).filter(Boolean)
          if (labels.length) answerText = labels.join(" · ")
        }

        return {
          participantId: participant._id,
          displayName: participant.displayName,
          answered: true,
          answerText,
          correct: Boolean(answer.correct),
        }
      })
    },
  },
  mounted() {
    this.loadData()
  },
  methods: {
    async loadData() {
      this.loading = true
      try {
        const [quizzes, activeSessions, history] = await Promise.all([
          api.liveQuiz.manage.fetchQuizzes(),
          api.liveQuiz.manage.fetchActiveSessions(),
          api.liveQuiz.manage.fetchHistory(),
        ])
        this.quizzes = quizzes || []
        this.activeSessions = activeSessions || []
        this.history = history || []
      } catch (error) {
        this.showToast(this.apiError(error, "Quiz-Daten konnten nicht geladen werden."), "error")
      } finally {
        this.loading = false
      }
    },
    createQuiz() {
      this.selectedQuiz = null
      this.editorOpen = true
    },
    editQuiz(quiz) {
      this.selectedQuiz = quiz
      this.editorOpen = true
    },
    async saveQuiz(payload) {
      this.savingQuiz = true
      try {
        if (payload._id) await api.liveQuiz.manage.updateQuiz(payload._id, payload)
        else await api.liveQuiz.manage.createQuiz(payload)
        this.editorOpen = false
        await this.loadData()
        this.showToast(payload.status === "published" ? "Quiz ist bereit für die nächste Live-Runde." : "Entwurf wurde gespeichert.")
      } catch (error) {
        this.showToast(this.apiError(error, "Quiz konnte nicht gespeichert werden."), "error")
      } finally {
        this.savingQuiz = false
      }
    },
    async duplicateQuiz(quiz) {
      try {
        const duplicate = await api.liveQuiz.manage.duplicateQuiz(quiz._id)
        await this.loadData()
        this.editQuiz(duplicate)
        this.showToast("Kopie wurde als Entwurf erstellt.")
      } catch (error) {
        this.showToast(this.apiError(error, "Quiz konnte nicht dupliziert werden."), "error")
      }
    },
    async archiveQuiz(quiz) {
      if (!window.confirm(`Möchtest du „${quiz.title}“ wirklich archivieren?`)) return
      try {
        await api.liveQuiz.manage.archiveQuiz(quiz._id)
        await this.loadData()
        this.showToast("Quiz wurde archiviert.")
      } catch (error) {
        this.showToast(this.apiError(error, "Quiz konnte nicht archiviert werden."), "error")
      }
    },
    async startSession(quiz) {
      if (this.startingQuizId) return
      this.startingQuizId = quiz._id
      try {
        const session = await api.liveQuiz.manage.createSession(quiz._id, true)
        this.$router.push({ name: "liveQuizHost", params: { sessionId: session._id } })
      } catch (error) {
        this.showToast(this.apiError(error, "Live-Session konnte nicht erstellt werden."), "error")
      } finally {
        this.startingQuizId = null
      }
    },
    resumeSession(session) {
      this.$router.push({ name: "liveQuizHost", params: { sessionId: session._id } })
    },
    async openResults(session) {
      this.resultsOpen = true
      this.resultsTab = "review"
      this.reviewQuestionIndex = 0
      this.reviewAnswersVisible = false
      this.loadingResults = true
      this.selectedResults = null
      try {
        this.selectedResults = await api.liveQuiz.manage.fetchResults(session._id)
      } catch (error) {
        this.resultsOpen = false
        this.showToast(this.apiError(error, "Resultate konnten nicht geladen werden."), "error")
      } finally {
        this.loadingResults = false
      }
    },
    statusCount(status) {
      return this.quizzes.filter((quiz) => quiz.status === status).length
    },
    filterCount(filter) {
      return filter === "all" ? this.quizzes.length : this.statusCount(filter)
    },
    statusLabel(status) {
      return { published: "Live bereit", draft: "Entwurf", archived: "Archiviert" }[status] || status
    },
    stateLabel(session) {
      const state = typeof session === "string" ? session : session.state
      if (session && session.pauseRequested) return "Pause nach dieser Frage"
      return { lobby: "Lobby offen", countdown: "Startet", question: "Frage läuft", reveal: "Auflösung", leaderboard: "Rangliste", paused: "Pausiert · sicher gespeichert" }[state] || "Live"
    },
    historyIcon(state) {
      if (state === "cancelled") return "mdi-cancel"
      if (state === "expired") return "mdi-clock-alert-outline"
      return "mdi-trophy-outline"
    },
    totalDuration(quiz) {
      return (quiz.questions || []).reduce((total, question) => total + Number(question.timeLimitSeconds || 60), 0)
    },
    formatCode(code) {
      const value = String(code || "")
      return value.length === 6 ? `${value.slice(0, 3)} ${value.slice(3)}` : value
    },
    formatDate(value) {
      return value ? this.$moment(value).format("DD.MM.YYYY · HH:mm") : "–"
    },
    formatRelativeDate(value) {
      if (!value) return "–"
      const date = this.$moment(value)
      return date.isSame(this.$moment(), "day") ? `heute um ${date.format("HH:mm")}` : date.format("DD.MM.YYYY")
    },
    formatScore(score) {
      return new Intl.NumberFormat("de-CH").format(Number(score || 0))
    },
    formatEstimate(value) {
      if (value == null || !Number.isFinite(Number(value))) return "–"
      return new Intl.NumberFormat("de-CH", { maximumFractionDigits: 4 }).format(Number(value))
    },
    reviewQuestionTypeLabel(type) {
      return { single: "Einzelwahl", multiple: "Mehrfachauswahl", estimate: "Schätzfrage" }[type] || "Frage"
    },
    reviewEstimateRule(question) {
      if (question.estimateMode === "tolerance") {
        return `Als richtig gewertet innerhalb von ±${this.formatEstimate(question.tolerancePercent)} %.`
      }
      return "Die Schätzung mit der kleinsten Abweichung gewinnt; Gleichstände werden gleich gewertet."
    },
    reviewAnswerTone(index) {
      return ["review-tone-coral", "review-tone-blue", "review-tone-amber", "review-tone-violet"][index % 4]
    },
    reviewAnswerIcon(index) {
      return ["mdi-triangle", "mdi-rhombus", "mdi-circle", "mdi-square"][index % 4]
    },
    previousReviewQuestion() {
      if (this.reviewQuestionIndex === 0) return
      this.reviewQuestionIndex -= 1
      this.reviewAnswersVisible = false
    },
    nextReviewQuestion() {
      if (this.reviewQuestionIndex >= this.reviewQuestions.length - 1) {
        this.resultsTab = "ranking"
        this.reviewAnswersVisible = false
        return
      }
      this.reviewQuestionIndex += 1
      this.reviewAnswersVisible = false
    },
    initials(name) {
      return String(name || "?")
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((part) => part.charAt(0).toUpperCase())
        .join("") || "?"
    },
    apiError(error, fallback) {
      return (error && error.response && error.response.data && error.response.data.error) || (error && error.message) || fallback
    },
    showToast(message, type = "success") {
      this.toastMessage = message
      this.toastType = type
      this.toastVisible = true
    },
  },
}
</script>

<style scoped>
.quiz-manage-page {
  --green: #58d388;
  --border: rgba(255, 255, 255, 0.1);
  position: relative;
  min-height: 100vh;
  overflow: hidden;
  color: #f5f8f6;
  background: radial-gradient(circle at 50% -15%, rgba(0, 150, 64, 0.18), transparent 34%), #080b0a;
  font-size: 1rem;
}
.manage-ambient { position: fixed; right: -16rem; top: 30%; width: 32rem; height: 32rem; border-radius: 50%; background: rgba(88, 211, 136, 0.07); filter: blur(6rem); pointer-events: none; }
.manage-header { position: relative; z-index: 4; display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; min-height: 5rem; padding: 0.75rem 1.25rem; border-bottom: 1px solid rgba(255, 255, 255, 0.07); background: rgba(8, 11, 10, 0.74); backdrop-filter: blur(1.25rem); }
.header-button { display: grid; width: 3rem; height: 3rem; place-items: center; border: 1px solid var(--border); border-radius: 1rem; color: #edf2ef; background: rgba(255, 255, 255, 0.045); }
.header-button .v-icon { color: currentColor; }
.brand-lockup { display: flex; align-items: center; gap: 0.7rem; }
.brand-lockup > img { display: block; width: 3.25rem; height: 2.5rem; object-fit: contain; }
.brand-lockup > div { display: flex; flex-direction: column; }
.brand-lockup strong { font-size: 0.875rem; letter-spacing: 0.12em; }
.brand-lockup small { color: #86948c; font-size: 0.75rem; }
.header-actions { display: flex; align-items: center; justify-content: flex-end; gap: 0.5rem; }
.player-link { font-size: 0.875rem; text-transform: none; }
.manage-shell { position: relative; z-index: 1; width: min(calc(100% - 2rem), 82rem); margin: 0 auto; padding: 3rem 0 5rem; }
.manage-hero { display: flex; align-items: flex-end; justify-content: space-between; gap: 2rem; }
.manage-kicker { display: inline-flex; align-items: center; gap: 0.5rem; color: var(--green); font-size: 0.75rem; font-weight: 900; letter-spacing: 0.16em; }
.manage-kicker i { width: 0.5rem; height: 0.5rem; border-radius: 50%; background: currentColor; box-shadow: 0 0 0 0.35rem rgba(88, 211, 136, 0.1); }
.manage-hero h1 { margin: 0.75rem 0 0; font-size: clamp(2.75rem, 6vw, 5rem); line-height: 1; letter-spacing: -0.055em; }
.manage-hero h1 em { color: var(--green); font-style: normal; }
.manage-hero p { max-width: 40rem; margin: 1rem 0 0; color: #9aa79f; font-size: 1rem; line-height: 1.6; }
.create-button { min-width: 13rem !important; border-radius: 1rem !important; color: #07100a !important; font-size: 1rem !important; font-weight: 900 !important; letter-spacing: 0 !important; text-transform: none !important; }
.metric-grid { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); gap: 0.75rem; margin-top: 2rem; }
.metric-card { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 0.875rem; min-height: 7.5rem; padding: 1rem; border: 1px solid var(--border); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.035); }
.metric-icon { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 1rem; background: rgba(255, 255, 255, 0.06); }
.metric-icon .v-icon { color: #98a69e; }
.metric-icon.published { background: rgba(88, 211, 136, 0.11); }
.metric-icon.published .v-icon,
.metric-icon.live .v-icon { color: var(--green); }
.metric-icon.draft { background: rgba(73, 163, 255, 0.1); }
.metric-icon.draft .v-icon { color: #71b6ff; }
.metric-icon.live { background: rgba(88, 211, 136, 0.1); }
.metric-card > div { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: baseline; }
.metric-card small { color: #8c9a92; font-size: 0.75rem; }
.metric-card strong { grid-row: 1 / span 2; grid-column: 2; font-size: 2rem; }
.metric-card p { margin: 0.2rem 0 0; color: #6f7d75; font-size: 0.75rem; }
.active-sessions-section,
.library-section,
.history-section { margin-top: 2.5rem; }
.section-heading { display: flex; align-items: flex-end; justify-content: space-between; gap: 1rem; margin-bottom: 1rem; }
.section-heading > div > span { color: var(--green); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.12em; }
.section-heading h2 { margin: 0.2rem 0 0; font-size: 1.75rem; letter-spacing: -0.03em; }
.section-heading p { margin: 0.35rem 0 0; color: #87958d; font-size: 0.875rem; }
.active-session-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.75rem; }
.active-session-card { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.875rem; min-height: 7rem; padding: 1rem; border: 1px solid rgba(88, 211, 136, 0.24); border-radius: 1.25rem; color: inherit; background: linear-gradient(120deg, rgba(88, 211, 136, 0.09), transparent 60%), rgba(255, 255, 255, 0.035); text-align: left; transition: border-color 160ms ease, translate 160ms ease; }
.active-session-card:hover { translate: 0 -0.125rem; border-color: rgba(88, 211, 136, 0.5); }
.active-session-card.paused { border-color: rgba(247, 200, 75, 0.25); background: linear-gradient(120deg, rgba(247, 200, 75, 0.09), transparent 60%), rgba(255, 255, 255, 0.035); }
.active-session-card.paused .live-orbit { background: rgba(247, 200, 75, 0.12); }
.active-session-card.paused .live-orbit .v-icon,
.active-session-card.paused small { color: #f7c84b; }
.active-session-card.paused .live-orbit i { border-color: rgba(247, 200, 75, 0.24); animation: none; }
.live-orbit { position: relative; display: grid; width: 3.75rem; height: 3.75rem; place-items: center; border-radius: 1.125rem; background: rgba(88, 211, 136, 0.12); }
.live-orbit .v-icon { color: var(--green); }
.live-orbit i { position: absolute; inset: -0.2rem; border: 1px solid rgba(88, 211, 136, 0.22); border-radius: 1.25rem; animation: livePulse 1.8s ease-in-out infinite; }
.active-session-card > div { display: flex; min-width: 0; flex-direction: column; }
.active-session-card small { color: var(--green); font-size: 0.75rem; font-weight: 800; }
.active-session-card strong { overflow: hidden; margin-top: 0.15rem; font-size: 1.125rem; text-overflow: ellipsis; white-space: nowrap; }
.active-session-card p { margin: 0.3rem 0 0; color: #8d9b93; font-size: 0.75rem; }
.active-session-card p span { color: #d6ded9; font-weight: 850; letter-spacing: 0.06em; }
.active-session-card > .v-icon { color: #6f7d75; }
.library-heading { align-items: center; }
.filter-pills { display: flex; flex-wrap: wrap; gap: 0.4rem; padding: 0.3rem; border: 1px solid var(--border); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.03); }
.filter-pills button { display: inline-flex; min-height: 2.25rem; align-items: center; gap: 0.4rem; padding: 0 0.75rem; border-radius: 0.625rem; color: #8e9b94; font-size: 0.75rem; font-weight: 800; }
.filter-pills button span { display: grid; min-width: 1.25rem; height: 1.25rem; place-items: center; border-radius: 0.4rem; background: rgba(255, 255, 255, 0.06); font-size: 0.75rem; }
.filter-pills button.active { color: #07100a; background: var(--green); }
.filter-pills button.active span { background: rgba(7, 16, 10, 0.14); }
.quiz-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.875rem; }
.quiz-skeleton { min-height: 19rem; overflow: hidden; border-radius: 1.25rem; }
.quiz-card { display: flex; min-height: 20rem; flex-direction: column; padding: 1.125rem; border: 1px solid var(--border); border-radius: 1.25rem; background: rgba(255, 255, 255, 0.035); transition: border-color 160ms ease, background 160ms ease, translate 160ms ease; }
.quiz-card:hover { translate: 0 -0.125rem; border-color: rgba(88, 211, 136, 0.28); background: rgba(255, 255, 255, 0.047); }
.quiz-card.card-archived { opacity: 0.68; }
.quiz-card-top { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.65rem; }
.quiz-card-icon { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 1rem; background: rgba(88, 211, 136, 0.1); }
.quiz-card-icon .v-icon { color: var(--green); }
.status-pill { display: inline-flex; width: fit-content; align-items: center; gap: 0.4rem; min-height: 1.75rem; padding: 0 0.6rem; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 2rem; color: #8f9c95; background: rgba(255, 255, 255, 0.035); font-size: 0.75rem; font-weight: 800; }
.status-pill i { width: 0.4rem; height: 0.4rem; border-radius: 50%; background: currentColor; }
.status-pill.status-published { border-color: rgba(88, 211, 136, 0.2); color: var(--green); background: rgba(88, 211, 136, 0.07); }
.status-pill.status-draft { border-color: rgba(73, 163, 255, 0.18); color: #71b6ff; background: rgba(73, 163, 255, 0.06); }
.card-menu-button { justify-self: end; color: #8b9891 !important; }
.quiz-card-copy { margin-top: 1rem; }
.quiz-card-copy h3 { margin: 0; overflow: hidden; font-size: 1.375rem; text-overflow: ellipsis; white-space: nowrap; }
.quiz-card-copy p { display: -webkit-box; min-height: 2.8rem; margin: 0.45rem 0 0; overflow: hidden; color: #8e9b94; font-size: 0.875rem; line-height: 1.55; -webkit-box-orient: vertical; -webkit-line-clamp: 2; }
.quiz-facts { display: flex; flex-wrap: wrap; gap: 0.75rem; margin-top: 1rem; }
.quiz-facts span { display: inline-flex; align-items: center; gap: 0.35rem; color: #a4b0aa; font-size: 0.75rem; }
.quiz-facts .v-icon { color: #6f7d75; font-size: 1rem; }
.quiz-card-footer { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; margin-top: auto; padding-top: 1rem; border-top: 1px solid rgba(255, 255, 255, 0.07); }
.quiz-card-footer > small { color: #6f7d75; font-size: 0.75rem; }
.quiz-card-footer > div { display: flex; align-items: center; gap: 0.4rem; }
.edit-button { border: 1px solid var(--border); color: #a3afa8 !important; }
.start-button { min-width: 8.5rem !important; border-radius: 0.75rem !important; color: #07100a !important; font-size: 0.75rem !important; font-weight: 900 !important; letter-spacing: 0 !important; text-transform: none !important; }
.start-button.v-btn--outlined { color: #aeb8b2 !important; }
.empty-library { display: flex; min-height: 20rem; align-items: center; justify-content: center; flex-direction: column; padding: 2rem; border: 1px dashed rgba(255, 255, 255, 0.13); border-radius: 1.25rem; text-align: center; }
.empty-library > span { display: grid; width: 4rem; height: 4rem; place-items: center; border-radius: 1.25rem; background: rgba(88, 211, 136, 0.08); }
.empty-library > span .v-icon { color: var(--green); }
.empty-library h3 { margin: 0.875rem 0 0; font-size: 1.25rem; }
.empty-library p { max-width: 30rem; margin: 0.5rem 0 1rem; color: #87958d; font-size: 0.875rem; }
.empty-library .v-btn { border-radius: 0.875rem; color: #07100a; font-size: 0.875rem; font-weight: 900; text-transform: none; }
.history-list { display: grid; gap: 0.5rem; }
.history-list > button { display: grid; grid-template-columns: auto minmax(0, 1fr) auto auto auto; align-items: center; gap: 0.75rem; min-height: 4.75rem; padding: 0.625rem 0.875rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 1rem; color: inherit; background: rgba(255, 255, 255, 0.025); text-align: left; }
.history-icon { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; background: rgba(88, 211, 136, 0.08); }
.history-icon .v-icon { color: var(--green); }
.history-list > button > div { display: flex; min-width: 0; flex-direction: column; }
.history-list > button > div strong { overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.history-list > button > div small { color: #77857d; font-size: 0.75rem; }
.history-fact { display: flex; align-items: flex-end; flex-direction: column; }
.history-fact strong { font-size: 1rem; }
.history-fact small { color: #77857d; font-size: 0.75rem; }
.history-code { padding: 0.35rem 0.5rem; border-radius: 0.5rem; color: #9eaaa3; background: rgba(0, 0, 0, 0.2); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.06em; }
.history-list > button > .v-icon { color: #647169; }
.results-card { max-height: 85vh; overflow: hidden; border: 1px solid var(--border); border-radius: 1.5rem !important; background: #101512 !important; }
.results-header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; padding: 1rem 1.125rem; border-bottom: 1px solid rgba(255, 255, 255, 0.08); }
.results-header > span { display: grid; width: 3rem; height: 3rem; place-items: center; border-radius: 1rem; background: rgba(247, 200, 75, 0.1); }
.results-header > span .v-icon { color: #f7c84b; }
.results-header > div { min-width: 0; }
.results-header small { color: #58d388; font-size: 0.75rem; font-weight: 850; letter-spacing: 0.1em; }
.results-header h2 { margin: 0.15rem 0 0; overflow: hidden; font-size: 1.25rem; text-overflow: ellipsis; white-space: nowrap; }
.results-body { padding: 1rem !important; background: #0c100e; }
.results-loading { display: flex; min-height: 16rem; align-items: center; justify-content: center; flex-direction: column; }
.results-loading p { margin: 0.75rem 0 0; color: #87958d; font-size: 0.875rem; }
.results-summary { display: grid; grid-template-columns: repeat(3, 1fr); gap: 0.5rem; margin-bottom: 1rem; }
.results-summary > div { display: flex; min-height: 5rem; align-items: center; justify-content: center; flex-direction: column; border: 1px solid var(--border); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.03); }
.results-summary span { font-size: 1.25rem; font-weight: 950; }
.results-summary small { color: #7d8a83; font-size: 0.75rem; }
.results-tabs { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; margin-bottom: 1rem; padding: 0.3rem; border: 1px solid var(--border); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.025); }
.results-tabs button { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.5rem; min-height: 2.75rem; padding: 0.5rem 0.75rem; border-radius: 0.65rem; color: #89968f; text-align: left; }
.results-tabs button .v-icon { color: currentColor; font-size: 1.125rem; }
.results-tabs button > span { font-size: 0.875rem; font-weight: 850; }
.results-tabs button > small { display: grid; min-width: 1.5rem; height: 1.5rem; place-items: center; border-radius: 0.5rem; background: rgba(255, 255, 255, 0.07); font-size: 0.75rem; font-weight: 850; }
.results-tabs button.active { color: #07100a; background: var(--green); }
.results-tabs button.active > small { background: rgba(7, 16, 10, 0.14); }
.question-review-flow { display: grid; gap: 0.75rem; }
.review-flow-progress { display: grid; gap: 0.5rem; padding: 0.75rem 0.875rem; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 0.875rem; background: rgba(88, 211, 136, 0.045); }
.review-flow-progress > div:first-child { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; }
.review-flow-progress strong { font-size: 0.875rem; }
.review-flow-progress span { color: #8e9b94; font-size: 0.75rem; }
.review-flow-progress > div:last-child { height: 0.375rem; overflow: hidden; border-radius: 1rem; background: rgba(255, 255, 255, 0.07); }
.review-flow-progress i { display: block; height: 100%; border-radius: inherit; background: var(--green); transition: width 180ms ease; }
.review-question-card { overflow: hidden; border: 1px solid rgba(255, 255, 255, 0.09); border-radius: 1.125rem; background: rgba(255, 255, 255, 0.025); }
.review-question-header { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.75rem; padding: 0.875rem; border-bottom: 1px solid rgba(255, 255, 255, 0.07); background: rgba(255, 255, 255, 0.025); }
.review-question-header > span { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.875rem; color: #07100a; background: var(--green); font-size: 0.875rem; font-weight: 950; }
.review-question-header > div:nth-child(2) { display: flex; min-width: 0; flex-direction: column; }
.review-question-header > div:nth-child(2) small { color: var(--green); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.06em; }
.review-question-header > div:nth-child(2) strong { margin-top: 0.15rem; overflow-wrap: anywhere; font-size: 1rem; line-height: 1.4; }
.review-response-total { display: flex; min-width: 4.5rem; align-items: center; flex-direction: column; padding: 0.45rem 0.65rem; border-radius: 0.75rem; background: rgba(0, 0, 0, 0.17); }
.review-response-total strong { color: var(--green); font-size: 1.125rem; }
.review-response-total small { color: #7f8c85; font-size: 0.75rem; }
.review-answer-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem; padding: 0.875rem; }
.review-answer { --review-color: #9ba8a1; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; min-height: 4.5rem; padding: 0.625rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.14); }
.review-answer.correct { border-color: rgba(88, 211, 136, 0.42); background: rgba(88, 211, 136, 0.075); }
.review-tone-coral { --review-color: #ff5f68; }
.review-tone-blue { --review-color: #49a3ff; }
.review-tone-amber { --review-color: #f7c84b; }
.review-tone-violet { --review-color: #b779ff; }
.review-answer-shape { display: grid; width: 2.75rem; height: 2.75rem; place-items: center; border-radius: 0.8rem; background: color-mix(in srgb, var(--review-color), transparent 82%); }
.review-answer-shape .v-icon { color: var(--review-color); font-size: 1.125rem; }
.review-answer > div { display: flex; min-width: 0; flex-direction: column; }
.review-answer > div strong { overflow-wrap: anywhere; font-size: 0.875rem; }
.review-answer > div small { margin-top: 0.15rem; color: #7e8b84; font-size: 0.75rem; }
.review-correct-badge { display: inline-flex; min-height: 1.75rem; align-items: center; gap: 0.25rem; padding: 0 0.5rem; border-radius: 2rem; color: var(--green); background: rgba(88, 211, 136, 0.1); font-size: 0.75rem; font-weight: 850; }
.review-correct-badge .v-icon { color: currentColor; font-size: 1rem; }
.review-estimate-answer { display: grid; grid-template-columns: auto minmax(0, 1fr); align-items: center; gap: 0.875rem; margin: 0.875rem; padding: 1rem; border: 1px solid rgba(88, 211, 136, 0.3); border-radius: 1rem; background: rgba(88, 211, 136, 0.07); }
.review-estimate-answer > span { display: grid; width: 3.5rem; height: 3.5rem; place-items: center; border-radius: 1rem; background: rgba(88, 211, 136, 0.13); }
.review-estimate-answer > span .v-icon { color: var(--green); font-size: 1.75rem; }
.review-estimate-answer > div { display: flex; min-width: 0; flex-direction: column; }
.review-estimate-answer small { color: var(--green); font-size: 0.75rem; font-weight: 850; letter-spacing: 0.08em; }
.review-estimate-answer strong { margin-top: 0.2rem; font-size: 1.75rem; }
.review-estimate-answer strong em { color: #98a69e; font-size: 1rem; font-style: normal; }
.review-estimate-answer p { margin: 0.35rem 0 0; color: #95a29b; font-size: 0.875rem; line-height: 1.5; }
.review-question-footer { display: flex; flex-wrap: wrap; gap: 0.75rem; padding: 0.625rem 0.875rem; border-top: 1px solid rgba(255, 255, 255, 0.06); }
.review-question-footer span { display: inline-flex; align-items: center; gap: 0.35rem; color: #87948d; font-size: 0.75rem; }
.review-question-footer .v-icon { color: #65736b; font-size: 1rem; }
.review-participant-section { border-top: 1px solid rgba(255, 255, 255, 0.07); }
.review-participant-toggle { display: grid; width: 100%; grid-template-columns: auto minmax(0, 1fr) auto auto; align-items: center; gap: 0.625rem; padding: 0.75rem 0.875rem; border: 0; color: #edf3ef; background: transparent; font-family: inherit; text-align: left; cursor: pointer; }
.review-participant-toggle > span:first-child { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; background: rgba(88, 211, 136, 0.09); }
.review-participant-toggle > span:first-child .v-icon { color: var(--green); font-size: 1.25rem; }
.review-participant-toggle > div { display: flex; min-width: 0; flex-direction: column; }
.review-participant-toggle strong { font-size: 0.875rem; }
.review-participant-toggle small { margin-top: 0.1rem; color: #839088; font-size: 0.75rem; }
.review-participant-toggle em { display: grid; min-width: 1.75rem; height: 1.75rem; place-items: center; border-radius: 0.6rem; color: var(--green); background: rgba(88, 211, 136, 0.09); font-size: 0.75rem; font-style: normal; font-weight: 900; }
.review-participant-toggle > .v-icon { color: #6f7d75; }
.review-participant-list { display: grid; gap: 0.4rem; max-height: 22rem; padding: 0 0.875rem 0.875rem; overflow-y: auto; }
.review-participant-row { display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.625rem; min-height: 3.75rem; padding: 0.5rem 0.625rem; border: 1px solid rgba(255, 255, 255, 0.07); border-radius: 0.875rem; background: rgba(0, 0, 0, 0.15); }
.review-participant-row.unanswered { opacity: 0.66; }
.review-participant-avatar { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: #07100a; background: #8cdca9; font-size: 0.75rem; font-weight: 900; }
.review-participant-row > div { display: flex; min-width: 0; flex-direction: column; }
.review-participant-row > div strong { font-size: 0.875rem; }
.review-participant-row > div small { margin-top: 0.1rem; overflow-wrap: anywhere; color: #9aa79f; font-size: 0.875rem; }
.review-participant-status { display: inline-flex; min-height: 1.875rem; align-items: center; gap: 0.25rem; padding: 0 0.5rem; border-radius: 2rem; color: #ff929d; background: rgba(255, 95, 104, 0.09); font-size: 0.75rem; font-weight: 850; white-space: nowrap; }
.review-participant-status.correct { color: var(--green); background: rgba(88, 211, 136, 0.1); }
.review-participant-status .v-icon { color: currentColor; font-size: 1rem; }
.review-participant-row.unanswered .review-participant-status { color: #8d9a93; background: rgba(255, 255, 255, 0.05); }
.review-participant-empty { display: flex; min-height: 6rem; align-items: center; justify-content: center; flex-direction: column; gap: 0.5rem; color: #7f8c85; font-size: 0.875rem; text-align: center; }
.review-participant-empty .v-icon { color: currentColor; }
.review-navigation { display: grid; grid-template-columns: minmax(7.5rem, 1fr) auto minmax(7.5rem, 1fr); align-items: center; gap: 0.75rem; padding: 0.75rem; border: 1px solid rgba(88, 211, 136, 0.18); border-radius: 1rem; background: rgba(16, 23, 19, 0.96); }
.review-navigation > button { display: inline-flex; min-height: 3rem; align-items: center; justify-content: center; gap: 0.4rem; padding: 0.5rem 0.875rem; border: 0; border-radius: 0.75rem; font-family: inherit; font-size: 0.875rem; font-weight: 900; cursor: pointer; }
.review-navigation > button .v-icon { color: currentColor; font-size: 1.125rem; }
.review-navigation > button:disabled { cursor: default; opacity: 0.35; }
.review-navigation > .review-nav-secondary { justify-self: start; border: 1px solid rgba(255, 255, 255, 0.1); color: #acb7b1; }
.review-nav-primary { justify-self: end; color: #07100a; background: var(--green); }
.review-navigation > span { display: flex; min-width: 4rem; align-items: center; flex-direction: column; }
.review-navigation > span strong { font-size: 0.875rem; }
.review-navigation > span small { color: #77857d; font-size: 0.75rem; }
.results-ranking { display: grid; gap: 0.45rem; }
.results-ranking > div { display: grid; grid-template-columns: 2rem 2.75rem minmax(0, 1fr) auto; align-items: center; gap: 0.65rem; min-height: 4rem; padding: 0.5rem 0.75rem; border: 1px solid rgba(255, 255, 255, 0.08); border-radius: 0.875rem; background: rgba(255, 255, 255, 0.025); }
.result-rank { color: #738078; font-size: 1rem; font-weight: 950; }
.result-avatar { display: grid; width: 2.5rem; height: 2.5rem; place-items: center; border-radius: 0.75rem; color: #07100a; background: #899f95; font-size: 0.75rem; font-weight: 900; }
.results-ranking > div > div { display: flex; min-width: 0; flex-direction: column; }
.results-ranking > div > div strong { overflow: hidden; font-size: 0.875rem; text-overflow: ellipsis; white-space: nowrap; }
.results-ranking > div > div small { color: #78857e; font-size: 0.75rem; }
.result-score { color: var(--green); font-size: 1rem; }
.results-ranking .rank-1 { border-color: rgba(247, 200, 75, 0.3); background: rgba(247, 200, 75, 0.055); }
.results-ranking .rank-1 .result-avatar { background: #f7c84b; }
.results-ranking .rank-2 .result-avatar { background: #c9d0cc; }
.results-ranking .rank-3 .result-avatar { background: #d28f61; }
.results-empty { display: flex; min-height: 12rem; align-items: center; justify-content: center; flex-direction: column; color: #718078; text-align: center; }
.results-empty .v-icon { color: currentColor; font-size: 2.5rem; }
.results-empty p { margin: 0.5rem 0 0; font-size: 0.875rem; }

@keyframes livePulse { 0%, 100% { opacity: 0.35; } 50% { opacity: 1; } }

@media (max-width: 59.9375rem) {
  .metric-grid { grid-template-columns: repeat(2, minmax(0, 1fr)); }
  .library-heading { align-items: flex-start; flex-direction: column; }
  .quiz-grid { grid-template-columns: 1fr; }
}

@media (max-width: 47.9375rem) {
  .manage-header { grid-template-columns: auto 1fr auto; min-height: 4.5rem; padding: 0.625rem 0.75rem; }
  .brand-lockup { justify-self: center; }
  .brand-lockup > img { width: 2.75rem; height: 2.125rem; }
  .player-link { display: none; }
  .brand-lockup strong { font-size: 0.75rem; }
  .manage-shell { width: min(calc(100% - 1.25rem), 44rem); padding: 1.75rem 0 3rem; }
  .manage-hero { align-items: stretch; flex-direction: column; gap: 1.25rem; }
  .manage-hero h1 { font-size: clamp(2.5rem, 12vw, 4rem); }
  .create-button { width: 100%; }
  .metric-grid { gap: 0.5rem; }
  .metric-card { min-height: 6.5rem; gap: 0.6rem; padding: 0.75rem; }
  .metric-icon { width: 2.5rem; height: 2.5rem; }
  .metric-card strong { font-size: 1.5rem; }
  .active-session-grid { grid-template-columns: 1fr; }
  .filter-pills { width: 100%; overflow-x: auto; flex-wrap: nowrap; }
  .filter-pills button { flex: 0 0 auto; }
  .quiz-card { min-height: 19rem; padding: 1rem; }
  .quiz-card-footer { align-items: flex-start; flex-direction: column; }
  .quiz-card-footer > div { width: 100%; }
  .quiz-card-footer .start-button { flex: 1; }
  .history-list > button { grid-template-columns: auto minmax(0, 1fr) auto; }
  .history-fact,
  .history-code { display: none; }
  .results-summary { gap: 0.35rem; }
  .results-summary span { font-size: 1rem; }
  .results-tabs button { padding-inline: 0.625rem; }
  .results-tabs button .v-icon { display: none; }
  .review-question-header { grid-template-columns: auto minmax(0, 1fr); }
  .review-response-total { grid-column: 1 / -1; min-width: 0; align-items: baseline; flex-direction: row; justify-content: center; gap: 0.35rem; }
  .review-answer-grid { grid-template-columns: 1fr; padding: 0.75rem; }
  .review-answer { grid-template-columns: auto minmax(0, 1fr); }
  .review-correct-badge { grid-column: 2; width: fit-content; }
  .review-estimate-answer { margin: 0.75rem; padding: 0.875rem; }
  .review-participant-toggle { grid-template-columns: auto minmax(0, 1fr) auto; }
  .review-participant-toggle > em { display: none; }
  .review-participant-row { grid-template-columns: auto minmax(0, 1fr); }
  .review-participant-status { grid-column: 2; width: fit-content; }
  .review-navigation { grid-template-columns: 1fr 1fr; }
  .review-navigation > span { display: none; }
  .review-navigation > button { width: 100%; }
  .review-nav-secondary,
  .review-nav-primary { justify-self: stretch; }
}

@media (prefers-reduced-motion: reduce) {
  .live-orbit i { animation: none; }
  .quiz-card,
  .active-session-card { transition: none; }
  .review-flow-progress i { transition: none; }
}
</style>
